import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import type { MasterclassState } from "@/types/masterclasses"

/**
 * OKS-54 — Masterclass status badge.
 *
 * Maps `state` → shadcn `Badge` variant + i18n label key. Used inline
 * on Index cards, Show header, and Form summary right-rail.
 *
 * UX Spec §6 a11y: pairs colour with text label — colour is never the
 * sole indicator. Locked variants (`secondary` for draft / `default`
 * for published) match UX Spec §2.1 (card footer).
 */
export interface MasterclassStatusBadgeProps {
  state: MasterclassState
  /** Optional `data-testid` override for scoped DOM queries. */
  "data-testid"?: string
}

export function MasterclassStatusBadge({
  state,
  "data-testid": testId,
}: MasterclassStatusBadgeProps) {
  const { t } = useTranslation()
  const variant = state === "published" ? "default" : "secondary"

  return (
    <Badge
      variant={variant}
      data-testid={testId ?? `masterclass-state-${state}`}
    >
      {t(`masterclasses.states.${state}`)}
    </Badge>
  )
}
